import React, { useState } from "react";
import { useUserApi } from "../../api/UserApi";
import { toast } from "react-toastify";
import { handleApiError } from "../../utils/handleApiError";
import { useUser } from "../../context/UserContext";

interface ChangePasswordModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const ChangePasswordModal: React.FC<ChangePasswordModalProps> = ({
  isOpen,
  onClose,
}) => {
  const { changePassword } = useUserApi();
  const {user} = useUser();
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  if (!isOpen) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");

    if (newPassword !== confirmPassword) {
      setError("Nytt passord og bekreftelse er ikke like.");
      return;
    }

    setLoading(true);
    try {
      await changePassword(
        user?.id!,
        currentPassword,
        newPassword,
      );

      toast.success("Passordet er oppdatert!");
      onClose();

      // reset fields
      setCurrentPassword("");
      setNewPassword("");
      setConfirmPassword("");
    } catch (err: any) {
      handleApiError(err, "Kunne ikke endre passord. Prøv igjen senere.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/40 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Modal Card */}
      <div className="relative z-10 bg-white/95 backdrop-blur-sm rounded-2xl shadow-lg p-8 w-full max-w-md mx-4 border border-orangeBrand/20">
        <h2 className="text-2xl font-bold text-center text-orangeBrand mb-2">
          🔒 Endre passord
        </h2>
        <p className="text-center text-textDark/70 mb-6">
          Oppdater passordet ditt
        </p>

        <form onSubmit={handleSubmit} className="flex flex-col gap-4">
          <div>
            <label className="block text-textDark font-medium mb-1">
              Nåværende passord
            </label>
            <input
              type="password"
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
              className="w-full border border-orangeBrand/30 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-orangeBrand"
              placeholder="••••••••"
              required
            />
          </div>

          <div>
            <label className="block text-textDark font-medium mb-1">
              Nytt passord
            </label>
            <input
              type="password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              className="w-full border border-orangeBrand/30 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-orangeBrand"
              placeholder="••••••••"
              required
            />
          </div>

          <div>
            <label className="block text-textDark font-medium mb-1">
              Bekreft nytt passord
            </label>
            <input
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              className="w-full border border-orangeBrand/30 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-orangeBrand"
              placeholder="••••••••"
              required
            />
          </div>

          {error && (
            <p className="text-red-500 text-sm text-center">{error}</p>
          )}

          <div className="flex gap-3 mt-2">
            <button
              type="button"
              onClick={onClose}
              className="w-1/2 border border-orangeBrand/30 text-orangeBrand font-semibold py-2 rounded-lg hover:bg-orangeBrand/5 transition"
            >
              Avbryt
            </button>

            <button
              type="submit"
              disabled={loading}
              className="w-1/2 bg-orangeBrand hover:bg-orangeHover text-white font-semibold py-2 rounded-lg transition"
            >
              {loading ? "Lagrer..." : "Endre passord"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ChangePasswordModal;
